import { Controller } from "@hotwired/stimulus"
import * as bootstrap from "../lib/bootstrap"

// Defensive cleanup: if no modal is showing, nothing should still be holding
// a backdrop or body lock. Guards against a stray overlay permanently
// blocking the UI if a hide is ever interrupted or duplicated.
function ensureModalTeardown() {
  if (!document.querySelector('.modal.show')) {
    document.querySelectorAll('.modal-backdrop').forEach(el => el.remove())
    document.body.classList.remove('modal-open')
    document.body.style.removeProperty('overflow')
    document.body.style.removeProperty('padding-right')
  }
}

export default class extends Controller {
  static targets = [
    "entryStep", "confirmStep",
    "entryFooter", "confirmFooter",
    "countrySelect", "numberInput", "notesInput", "protocolCheck",
    "numberError", "continueBtn", "submitBtn",
    "confirmCountry", "confirmNumber", "confirmPlayerName", "confirmPlayerId",
    "playerName", "playerId"
  ]

  static values = {
    validateUrl: String,
    submitUrl: String,
    playerName: String,
    playerId: String
  }

  connect() {
    this.playerNameTarget.textContent = this.playerNameValue
    this.playerIdTarget.textContent   = this.playerIdValue
    this.element.addEventListener('show.bs.modal', () => this._resetToStep1())
    this.element.addEventListener('hidden.bs.modal', ensureModalTeardown)
  }

  // ── Step 1 interactions ───────────────────────────────────────────

  onCountryChange() {
    const hasCountry = this.countrySelectTarget.value !== ''
    this.numberInputTarget.disabled = !hasCountry
    if (!hasCountry) {
      this.numberInputTarget.value = ''
      this._clearNumberError()
      this._validateForm()
    } else {
      this.numberInputTarget.focus()
      // Re-validate any already-entered number against the new country rather
      // than leaving it showing as valid/invalid for the previous country.
      this._debounceValidate()
    }
  }

  onNumberInput() {
    // The previous AJAX validation result no longer applies to this input;
    // keep Continue disabled until the new debounced validation resolves.
    this._clearNumberError()
    this._pendingValidation = true
    this._validateForm()
    this._debounceValidate()
  }

  onNotesInput() {
    this._validateForm()
  }

  onProtocolChange() {
    this._validateForm()
  }

  // ── Navigation ────────────────────────────────────────────────────

  goToConfirm() {
    const country = this.countrySelectTarget.value
    const number  = this.numberInputTarget.value.trim()
    const countryLabels = { US: 'United States (+1)', CA: 'Canada (+1)', PR: 'Puerto Rico (+1)', GB: 'United Kingdom (+44)', AU: 'Australia (+61)' }

    this.confirmPlayerNameTarget.textContent = this.playerNameValue
    this.confirmPlayerIdTarget.textContent   = this.playerIdValue
    this.confirmCountryTarget.textContent = countryLabels[country] || country
    this.confirmNumberTarget.textContent  = number

    this._showStep(2)
  }

  backToEntry() {
    this._showStep(1)
  }

  // ── Submission ────────────────────────────────────────────────────

  async submitForm() {
    this.submitBtnTarget.disabled = true
    this.submitBtnTarget.textContent = 'Adding...'

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

    try {
      const response = await fetch(this.submitUrlValue, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken,
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          number:         this.numberInputTarget.value.trim(),
          alpha_2_code:   this.countrySelectTarget.value,
          internal_notes: this.notesInputTarget.value.trim(),
          protocol_ack:   this.protocolCheckTarget.checked
        })
      })

      const data = await response.json()

      if (data.success) {
        // Reload the phone tab only AFTER the modal has fully hidden. _reloadPhoneTab
        // replaces #playerPhoneNumbers (which contains this modal) — doing it mid-hide
        // rips out the modal element before Bootstrap finishes its teardown, orphaning
        // the .modal-backdrop and body.modal-open (the leftover gray overlay).
        const bsModal = bootstrap.Modal.getOrCreateInstance(this.element)
        this.element.addEventListener('hidden.bs.modal', () => {
          this._resetSubmitButton()
          this._reloadPhoneTab()
        }, { once: true })
        bsModal.hide()
      } else {
        alert('Error: ' + (data.error || 'Unknown error. Please try again.'))
        this._resetSubmitButton()
      }
    } catch (err) {
      console.error('Error submitting phone number:', err)
      alert('Network error. Please try again.')
      this._resetSubmitButton()
    }
  }

  // ── Private ───────────────────────────────────────────────────────

  _resetSubmitButton() {
    this.submitBtnTarget.disabled = false
    this.submitBtnTarget.textContent = 'Proceed to Add'
  }

  _debounceValidate() {
    clearTimeout(this._debounceTimer)
    this._debounceTimer = setTimeout(() => this._runAjaxValidation(), 400)
  }

  async _runAjaxValidation() {
    const number     = this.numberInputTarget.value.trim()
    const alpha2Code = this.countrySelectTarget.value

    // Only the most recently issued validation is allowed to apply its result —
    // an older, slower response arriving after a newer one must not overwrite it.
    const requestId = (this._validationRequestId = (this._validationRequestId || 0) + 1)

    if (!number || !alpha2Code) {
      this._pendingValidation = false
      this._clearNumberError()
      this._validateForm()
      return
    }

    try {
      const url = `${this.validateUrlValue}?number=${encodeURIComponent(number)}&alpha_2_code=${encodeURIComponent(alpha2Code)}`
      const resp = await fetch(url, { headers: { 'Accept': 'application/json' } })
      const data = await resp.json()

      if (requestId !== this._validationRequestId) return // superseded by a newer request

      if (data.valid) {
        this._clearNumberError()
      } else {
        this._showNumberError(data.error || 'Invalid phone number')
      }
    } catch (err) {
      if (requestId !== this._validationRequestId) return
      console.error('Validation error:', err)
    }

    this._pendingValidation = false
    this._validateForm()
  }

  _validateForm() {
    const countrySelected = this.countrySelectTarget.value !== ''
    const numberFilled    = this.numberInputTarget.value.trim() !== ''
    const notesFilled     = this.notesInputTarget.value.trim() !== ''
    const protocolChecked = this.protocolCheckTarget.checked
    const noNumberError   = !this.numberInputTarget.classList.contains('is-invalid')
    const notPending      = !this._pendingValidation

    this.continueBtnTarget.disabled = !(countrySelected && numberFilled && notesFilled && protocolChecked && noNumberError && notPending)
  }

  _showNumberError(message) {
    this.numberInputTarget.classList.add('is-invalid')
    this.numberErrorTarget.textContent = message
  }

  _clearNumberError() {
    this.numberInputTarget.classList.remove('is-invalid')
    this.numberErrorTarget.textContent = ''
  }

  _showStep(step) {
    const isStep1 = step === 1
    this.entryStepTarget.style.display     = isStep1 ? '' : 'none'
    this.entryFooterTarget.style.display   = isStep1 ? '' : 'none'
    this.confirmStepTarget.style.display   = isStep1 ? 'none' : ''
    this.confirmFooterTarget.style.display = isStep1 ? 'none' : ''
  }

  _resetToStep1() {
    this._showStep(1)
    this.countrySelectTarget.value   = ''
    this.numberInputTarget.value     = ''
    this.numberInputTarget.disabled  = true
    this.notesInputTarget.value      = ''
    this.protocolCheckTarget.checked = false
    this._pendingValidation = false
    this._validationRequestId = (this._validationRequestId || 0) + 1
    this._clearNumberError()
    this.continueBtnTarget.disabled = true
  }

  // Matches the $.get pattern used in adminRouter.js players() tab loading.
  // Does NOT call playerTabs() — that function is webpack-bundle-scoped and not globally accessible.
  // History/code click handlers are delegated on document and survive DOM replacement.
  // Tooltips are re-initialized directly since that is all playerPhoneNumbers() does on load.
  _reloadPhoneTab() {
    const playerId = this.playerIdValue
    $.get(playerId + '/player_phone_numbers', function(data) {
      $('#playerPhoneNumbers').html(data)
      document.querySelectorAll('[data-bs-toggle="tooltip"]').forEach(el => new bootstrap.Tooltip(el))
    })
  }
}
